import {
  AuthContainer,
  AuthHeader,
  AuthErrorBanner,
  AuthFormField,
  AuthSubmitButton,
  AuthLink,
  AuthResetPasswordVerificationStep,
} from '../atoms';
import type { FormEvent } from 'react';
import { theme } from '../../styles/theme';

type ResetPasswordMethod = 'code' | 'link';

export interface ForgotPasswordFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  error?: string;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  submitButtonText?: string;
  loadingButtonText?: string;
  backToSignInText?: string;
  backToSignInUrl?: string;
  showVerificationStep?: boolean;
  resetPasswordMethod?: ResetPasswordMethod;
  onVerifyCode?: (code: string) => Promise<void>;
  onResendEmail?: () => Promise<void>;
}

/**
 * Pre-built forgot password form component (UI only, no business logic).
 */
export function ForgotPasswordForm({
  email,
  onEmailChange,
  onSubmit,
  error,
  loading = false,
  title = 'Forgot Password?',
  subtitle = "Enter your email address and we'll send you a {method} to reset your password.",
  emailLabel = 'Email',
  emailPlaceholder = 'example@email.com',
  submitButtonText = 'Reset Password',
  loadingButtonText = 'Sending...',
  backToSignInText = 'Remember your password?',
  backToSignInUrl = '/sign-in',
  showVerificationStep = false,
  resetPasswordMethod = 'code',
  onVerifyCode,
  onResendEmail,
}: ForgotPasswordFormProps) {
  const displaySubtitle = subtitle.replace('{method}', resetPasswordMethod);

  return (
    <AuthContainer>
      <AuthHeader title={title} subtitle={displaySubtitle} />

      <AuthErrorBanner error={error || ''} />

      {showVerificationStep ? (
        <AuthResetPasswordVerificationStep
          email={email}
          method={resetPasswordMethod}
          onVerifyCode={onVerifyCode}
          onResendEmail={onResendEmail || (async () => {})}
        />
      ) : (
        <form
          onSubmit={onSubmit}
          noValidate
          style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[6] }}
        >
          <AuthFormField
            id="email"
            type="email"
            label={emailLabel}
            placeholder={emailPlaceholder}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            required
            autoComplete="email"
          />

          <AuthSubmitButton isLoading={loading} disabled={loading}>
            {loading ? loadingButtonText : submitButtonText}
          </AuthSubmitButton>
        </form>
      )}

      {!showVerificationStep && (
        <AuthLink text={backToSignInText} linkText="Back to Sign In" href={backToSignInUrl} />
      )}
    </AuthContainer>
  );
}
